import { api } from './api.js';
import { getLocalDate } from '../utils/date-utils';
import type { CourseResponse, SharedCourseResponse } from '@xdface-vault/shared';
import type { HighlightColor } from '../lib/highlight-colors.js';

export interface LessonResponse {
  courseId: string;
  lessonNumber: number;
  lessonOrder?: number;
  moduleId?: string;
  title: string;
  content?: string;
  s3Key?: string;
  youtubeUrl?: string;
  link?: string;
  durationMinutes?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ModuleResponse {
  moduleId: string;
  courseId: string;
  title: string;
  description?: string;
  moduleOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CourseCreateInput {
  title: string;
  description: string;
  source?: string;
  tags?: string[];
}

export interface CourseUpdateInput {
  title?: string;
  description?: string;
  source?: string | null;
  published?: boolean;
  tags?: string[];
  hasModules?: boolean;
  coverImageKey?: string | null;
  expectedVersion: number;
}

export interface ModuleCreateInput {
  title: string;
  description?: string;
}

export interface ModuleUpdateInput {
  title?: string;
  description?: string;
}

export interface LessonCreateInput {
  title: string;
  content?: string;
  s3Key?: string;
  youtubeUrl?: string;
  link?: string;
  moduleId?: string;
}

export interface LessonUpdateInput {
  title?: string;
  content?: string;
  s3Key?: string;
  youtubeUrl?: string | null;
  link?: string | null;
  durationMinutes?: number;
  moduleId?: string | null;
}

interface ListResponse<T> {
  items: T[];
  count: number;
}

export interface LessonProgress {
  lessonNum: number;
  completedAt: string;
}

export interface HighlightResponse {
  id: string;
  text: string;
  color: HighlightColor;
  quote?: string;
  startOffset?: number;
  endOffset?: number;
  note?: string;
  prefix?: string;
  suffix?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface HighlightCreateInput {
  text: string;
  color: HighlightColor;
  quote?: string;
  startOffset?: number;
  endOffset?: number;
  /** Optional reader note (max 500 chars). */
  note?: string;
  /** Text context around the selection, used to re-anchor after content edits. */
  prefix?: string;
  suffix?: string;
}

export interface HighlightUpdateInput {
  color?: HighlightColor;
  /** Pass an empty string to remove the note. */
  note?: string;
}

export const courseService = {
  async listCourses(): Promise<CourseResponse[]> {
    const res = await api.get<ListResponse<CourseResponse>>('/courses');
    return res.items;
  },

  async listSharedCourses(): Promise<SharedCourseResponse[]> {
    return api.get<SharedCourseResponse[]>('/courses/shared');
  },

  async getCourse(
    id: string,
  ): Promise<CourseResponse & { isShared?: boolean; ownerEmail?: string }> {
    return api.get<CourseResponse & { isShared?: boolean; ownerEmail?: string }>(`/courses/${id}`);
  },

  async grantCourseAccess(
    courseId: string,
    email: string,
  ): Promise<{ shared: boolean; recipient: string; courseTitle: string }> {
    return api.post(`/courses/${courseId}/grant`, { email });
  },

  async revokeGrantCourseAccess(
    courseId: string,
    email: string,
  ): Promise<{ revoked: boolean; recipient: string; courseTitle: string }> {
    return api.delete(`/courses/${courseId}/grant`, { email });
  },

  async createCourse(input: CourseCreateInput): Promise<CourseResponse> {
    return api.post<CourseResponse>('/courses', input);
  },

  async updateCourse(id: string, input: CourseUpdateInput): Promise<CourseResponse> {
    return api.put<CourseResponse>(`/courses/${id}`, input);
  },

  async deleteCourse(id: string): Promise<void> {
    await api.delete(`/courses/${id}`);
  },

  async getCourseLessons(courseId: string): Promise<LessonResponse[]> {
    const res = await api.get<ListResponse<LessonResponse>>(`/courses/${courseId}/lessons`);
    return res.items;
  },

  async getLesson(courseId: string, lessonNum: number): Promise<LessonResponse> {
    return api.get<LessonResponse>(`/courses/${courseId}/lessons/${lessonNum}`);
  },

  async createLesson(courseId: string, input: LessonCreateInput): Promise<LessonResponse> {
    return api.post<LessonResponse>(`/courses/${courseId}/lessons`, input);
  },

  async updateLesson(
    courseId: string,
    lessonNum: number,
    input: LessonUpdateInput,
  ): Promise<LessonResponse> {
    return api.put<LessonResponse>(`/courses/${courseId}/lessons/${lessonNum}`, input);
  },

  async deleteLesson(courseId: string, lessonNum: number): Promise<void> {
    await api.delete(`/courses/${courseId}/lessons/${lessonNum}`);
  },

  async reorderLessons(courseId: string, order: number[]): Promise<{ reordered: boolean }> {
    return api.patch<{ reordered: boolean }>(`/courses/${courseId}/lessons`, { order });
  },

  async listModules(courseId: string): Promise<ModuleResponse[]> {
    const res = await api.get<{ items: ModuleResponse[]; count: number }>(
      `/courses/${courseId}/modules`,
    );
    return res.items;
  },

  async createModule(courseId: string, input: ModuleCreateInput): Promise<ModuleResponse> {
    return api.post<ModuleResponse>(`/courses/${courseId}/modules`, input);
  },

  async updateModule(
    courseId: string,
    moduleId: string,
    input: ModuleUpdateInput,
  ): Promise<ModuleResponse> {
    return api.put<ModuleResponse>(`/courses/${courseId}/modules/${moduleId}`, input);
  },

  async deleteModule(courseId: string, moduleId: string): Promise<void> {
    await api.delete(`/courses/${courseId}/modules/${moduleId}`);
  },

  async reorderModules(courseId: string, order: string[]): Promise<{ reordered: boolean }> {
    return api.patch<{ reordered: boolean }>(`/courses/${courseId}/modules`, { order });
  },

  async shareCourse(
    courseId: string,
    ttlDays: 30 | 60 | 90 | 180 | 365,
  ): Promise<{ shareUrl: string; token: string; expiresAt: string; ttlDays: number }> {
    return api.post(`/courses/${courseId}/share`, { ttlDays, localDate: getLocalDate() });
  },

  async revokeCourseShare(courseId: string): Promise<void> {
    await api.delete(`/courses/${courseId}/share`);
  },

  async getPublicCourse(
    token: string,
  ): Promise<CourseResponse & { lessons: LessonResponse[]; modules: ModuleResponse[] }> {
    const url = `https://vault.xdface.net/api/public/course/${token}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error((error as { message?: string }).message || 'Request failed');
    }
    const json = await response.json();
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as CourseResponse & { lessons: LessonResponse[]; modules: ModuleResponse[] };
    }
    return json as CourseResponse & { lessons: LessonResponse[]; modules: ModuleResponse[] };
  },

  async getCourseProgress(courseId: string): Promise<{ items: LessonProgress[]; count: number }> {
    return api.get(`/courses/${courseId}/progress`);
  },

  async markLessonComplete(
    courseId: string,
    lessonNum: number,
  ): Promise<{ lessonNum: number; completedAt: string; courseCompleted: boolean }> {
    return api.post(`/courses/${courseId}/lessons/${lessonNum}/complete`, {
      localDate: getLocalDate(),
    });
  },

  async unmarkLessonComplete(
    courseId: string,
    lessonNum: number,
  ): Promise<{ lessonNum: number; unmarked: boolean }> {
    return api.delete(`/courses/${courseId}/lessons/${lessonNum}/complete`);
  },

  async getLessonHighlights(courseId: string, lessonNum: number): Promise<HighlightResponse[]> {
    const res = await api.get<{ items: HighlightResponse[]; count: number }>(
      `/courses/${courseId}/lessons/${lessonNum}/highlights`,
    );
    // Rows stored before the palette unification may still carry 'pink' —
    // alias it to 'red' on read (pink has no --xd-hl-* token).
    return res.items.map((h) =>
      (h.color as string) === 'pink' ? { ...h, color: 'red' as const } : h,
    );
  },

  async createHighlight(
    courseId: string,
    lessonNum: number,
    input: HighlightCreateInput,
    expectedUserId?: string,
  ): Promise<HighlightResponse> {
    return api.post(
      `/courses/${courseId}/lessons/${lessonNum}/highlights`,
      input,
      ...(expectedUserId ? [expectedUserId] : []),
    );
  },

  async updateHighlight(
    courseId: string,
    lessonNum: number,
    highlightId: string,
    patch: HighlightUpdateInput,
    expectedUserId?: string,
  ): Promise<HighlightResponse> {
    return api.patch(
      `/courses/${courseId}/lessons/${lessonNum}/highlights/${highlightId}`,
      patch,
      ...(expectedUserId ? [expectedUserId] : []),
    );
  },

  async deleteHighlight(
    courseId: string,
    lessonNum: number,
    id: string,
    expectedUserId?: string,
  ): Promise<void> {
    await api.delete(
      `/courses/${courseId}/lessons/${lessonNum}/highlights/${id}`,
      ...(expectedUserId ? [undefined, expectedUserId] : []),
    );
  },
};
